/**
 * Customer Segment Export Utilities
 * Handles CSV export and Mailchimp integration for customer segments
 */

/**
 * Convert customer data to CSV format
 * @param {Array} customers - Array of customer objects
 * @param {Array} fields - Fields to include in export
 * @returns {string} CSV string
 */
export const customersToCSV = (customers, fields = null) => {
  if (!customers || customers.length === 0) {
    return '';
  }

  // Default fields if not specified
  const defaultFields = [
    { key: 'email', label: 'Email' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'currentRfm.segment', label: 'RFM Segment' },
    { key: 'currentRfm.recencyScore', label: 'Recency Score' },
    { key: 'currentRfm.frequencyScore', label: 'Frequency Score' },
    { key: 'currentRfm.monetaryScore', label: 'Monetary Score' },
    { key: 'totalOrders', label: 'Total Orders' },
    { key: 'totalSpent', label: 'Total Spent' },
    { key: 'averageOrderValue', label: 'Average Order Value' },
    { key: 'lastOrderDate', label: 'Last Order Date' },
    { key: 'shopSource', label: 'Shop' },
  ];

  const exportFields = fields || defaultFields;

  // Create CSV header
  const header = exportFields.map(f => f.label).join(',');

  // Create CSV rows
  const rows = customers.map(customer => {
    return exportFields.map(field => {
      const value = getNestedValue(customer, field.key);
      
      // Handle different data types
      if (value === null || value === undefined) {
        return '';
      }
      
      if (typeof value === 'number') {
        return value;
      }
      
      if (value instanceof Date) {
        return value.toISOString().split('T')[0];
      }
      
      // Escape commas and quotes in strings
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return stringValue;
    }).join(',');
  });

  return [header, ...rows].join('\n');
};

/**
 * Get nested object value by path
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot-separated path (e.g., 'currentRfm.segment')
 * @returns {*} Value at path
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV content
 * @param {string} filename - Filename for download
 */
export const downloadCSV = (csvContent, filename = 'export.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Export customers to CSV and download
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentName - Name of segment for filename
 * @param {Array} fields - Optional custom fields
 */
export const exportCustomersToCSV = (customers, segmentName = 'customers', fields = null) => {
  const csv = customersToCSV(customers, fields);
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `${segmentName.toLowerCase().replace(/\s+/g, '_')}_${timestamp}.csv`;
  downloadCSV(csv, filename);
};

/**
 * Format customers for Mailchimp import
 * Mailchimp requires specific format: Email Address, First Name, Last Name, Tags
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentTag - Tag to apply to all customers
 * @returns {string} Mailchimp-formatted CSV
 */
export const customersToMailchimpCSV = (customers, segmentTag = '') => {
  if (!customers || customers.length === 0) {
    return '';
  }

  // Mailchimp CSV format
  const header = 'Email Address,First Name,Last Name,Tags';

  const rows = customers.map(customer => {
    const email = customer.email || '';
    const firstName = customer.firstName || '';
    const lastName = customer.lastName || '';
    
    // Build tags
    const tags = [];
    if (segmentTag) tags.push(segmentTag);
    if (customer.currentRfm?.segment) tags.push(customer.currentRfm.segment);
    if (customer.shopSource) tags.push(customer.shopSource);
    
    const tagsString = tags.join(';');
    
    // Escape fields if needed
    const escapeField = (field) => {
      if (field.includes(',') || field.includes('"')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    return [
      escapeField(email),
      escapeField(firstName),
      escapeField(lastName),
      escapeField(tagsString)
    ].join(',');
  });

  return [header, ...rows].join('\n');
};

/**
 * Export customers for Mailchimp and download
 * @param {Array} customers - Array of customer objects
 * @param {string} segmentName - Name of segment
 */
export const exportCustomersToMailchimp = (customers, segmentName = 'segment') => {
  const csv = customersToMailchimpCSV(customers, segmentName);
  const timestamp = new Date().toISOString().split('T')[0];
  const filename = `mailchimp_${segmentName.toLowerCase().replace(/\s+/g, '_')}_${timestamp}.csv`;
  downloadCSV(csv, filename);
};

/**
 * Build segment query based on filters
 * @param {Object} filters - Filter object
 * @returns {Object} Query configuration
 */
export const buildSegmentQuery = (filters) => {
  const query = {
    segment: filters.segment || null,
    shopSource: filters.shopSource || 'all',
    minOrders: filters.minOrders || null,
    maxOrders: filters.maxOrders || null,
    minSpent: filters.minSpent || null,
    maxSpent: filters.maxSpent || null,
    minRecency: filters.minRecency || null,
    maxRecency: filters.maxRecency || null,
    campaignInteraction: filters.campaignInteraction || null,
    dateRange: filters.dateRange || null,
  };

  return query;
};

/**
 * Filter customers based on query
 * @param {Array} customers - Array of all customers
 * @param {Object} query - Query object from buildSegmentQuery
 * @returns {Array} Filtered customers
 */
export const filterCustomers = (customers, query) => {
  return customers.filter(customer => {
    // Segment filter
    if (query.segment && customer.currentRfm?.segment !== query.segment) {
      return false;
    }

    // Shop source filter
    if (query.shopSource !== 'all' && customer.shopSource !== query.shopSource) {
      return false;
    }

    // Order count filters
    if (query.minOrders !== null && (customer.totalOrders || 0) < query.minOrders) {
      return false;
    }
    if (query.maxOrders !== null && (customer.totalOrders || 0) > query.maxOrders) {
      return false;
    }

    // Spent filters
    if (query.minSpent !== null && (customer.totalSpent || 0) < query.minSpent) {
      return false;
    }
    if (query.maxSpent !== null && (customer.totalSpent || 0) > query.maxSpent) {
      return false;
    }

    // Recency filters (days since last order)
    if (query.minRecency !== null || query.maxRecency !== null) {
      const recency = customer.currentRfm?.recency || 999;
      if (query.minRecency !== null && recency < query.minRecency) {
        return false;
      }
      if (query.maxRecency !== null && recency > query.maxRecency) {
        return false;
      }
    }

    // Campaign interaction filter
    if (query.campaignInteraction) {
      const hasCampaignOrders = (customer.totalCampaignOrders || 0) > 0;
      if (!hasCampaignOrders) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Get export statistics
 * @param {Array} customers - Filtered customers to export
 * @returns {Object} Statistics object
 */
export const getExportStats = (customers) => {
  if (!customers || customers.length === 0) {
    return {
      totalCustomers: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
      averageOrders: 0,
      shopBreakdown: {},
      segmentBreakdown: {},
    };
  }

  const stats = {
    totalCustomers: customers.length,
    totalRevenue: 0,
    totalOrders: 0,
    shopBreakdown: {},
    segmentBreakdown: {},
  };

  customers.forEach(customer => {
    stats.totalRevenue += customer.totalSpent || 0;
    stats.totalOrders += customer.totalOrders || 0;

    // Shop breakdown
    const shop = customer.shopSource || 'Unknown';
    stats.shopBreakdown[shop] = (stats.shopBreakdown[shop] || 0) + 1;

    // Segment breakdown
    const segment = customer.currentRfm?.segment || 'Unknown';
    stats.segmentBreakdown[segment] = (stats.segmentBreakdown[segment] || 0) + 1;
  });

  stats.averageOrderValue = stats.totalOrders > 0 ? stats.totalRevenue / stats.totalOrders : 0;
  stats.averageOrders = stats.totalCustomers > 0 ? stats.totalOrders / stats.totalCustomers : 0;

  return stats;
};

/**
 * Validate email for export
 * @param {string} email - Email to validate
 * @returns {boolean} Is valid
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Clean customer data for export
 * Removes customers with invalid emails and duplicates
 * @param {Array} customers - Array of customers
 * @returns {Array} Cleaned customers
 */
export const cleanCustomersForExport = (customers) => {
  const seen = new Set();
  
  return customers.filter(customer => {
    // Must have valid email
    if (!isValidEmail(customer.email)) {
      return false;
    }

    // Remove duplicates
    if (seen.has(customer.email.toLowerCase())) {
      return false;
    }

    seen.add(customer.email.toLowerCase());
    return true;
  });
};
